import i18nFactory from '@yandex-int/i18n';
import { compact, get, identity, memoize } from 'lodash';
import moment from 'moment';
import { Query } from 'redux-little-router';
import { createSelector } from 'reselect';

import { AlertType } from '../../components/Alert/Alert';
import { SelectorItems } from '../../components/CampaignAndGroupSelector/Selector/Selector';
import { AdditionalFilter } from '../../components/GridScreenTitle/GridScreenTitle';
import { getBannerColumns } from '../../declarations/banner/banner';
import { getCampaignColumns } from '../../declarations/campaign/campaign';
import { getFeedFilterColumns } from '../../declarations/feedFilter/feedFilter';
import {
    getGroupedColumnsGoals,
    GroupedColumnKey
} from '../../declarations/goals/goals';
import { getGroupColumns } from '../../declarations/group/group';
import { getPhraseColumns } from '../../declarations/phrase/phrase';
import { getRetargetingColumns } from '../../declarations/targeting/targeting';
import { getUsersProfileColumns } from '../../declarations/usersProfile/usersProfile';
import { RoutesPath } from '../../routes/index';
import {
    isBannerOrganizationEditable,
    isBannerPriceEditable
} from '../../service/BannerHelpers/BannerHelpers';
import {
    arrangeInOrder,
    getActiveItemsIds,
    getGoalsSettingsForColumnsSettingsEditor
} from '../../service/DeclarationHelper';
import {
    findAdditionalCriteriaByExpression,
    flattenContainer,
    getFilterCriteriaContainersByView
} from '../../service/FilterHelpers/FilterHelpers';
import {
    getAdditionalFilterItems,
    getCalcTypeFilterItems,
    getCampaignsTitleData,
    getGridAlertMessage,
    getGridAlertMessageType,
    getHintForTabMenuLink,
    getTitleDataForView
} from '../../service/GridHelpers/GridHelpers';
import {
    getGridItemsActions,
    getTextForItemsGroupsActionTooltipText,
    isGroupActionAvailable,
    isSelectedMoreThenInGrid
} from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { GridScreenLinkBuilder } from '../../service/GridScreenLinkBuilder';
import { LangUtils } from '../../service/Lang/Lang';
import { PricesWizardHelpers } from '../../service/PricesWizardHelpers/PricesWizardHelpers';
import { bannerPreviewSizes } from '../../typings/banners';
import {
    AccessRights,
    banners,
    campaigns,
    ClientInfo,
    clients,
    ColumnsSettingsViewDict,
    DateRangePreset,
    deals,
    feedFilters,
    groups,
    logs,
    OperatorData,
    phrases,
    reportsClient,
    reportsDomain,
    retargetings,
    Tld,
    usersProfile,
    WalletData
} from '../../typings/common';
import { ConstData } from '../../typings/constants';
import {
    AdditionalFiltersDict,
    Banner,
    CalcTypeFilter,
    Campaign,
    CampaignsCalcTypeFilter,
    CampaignType,
    FilterGridViewDict,
    GridRow,
    Group,
    GroupBaseInfo,
    GroupItemActionAccess,
    GroupType,
    Phrase,
    Retargeting
} from '../../typings/domain';
import {
    Criteria,
    CriteriaContainer,
    FilterPartialExpression
} from '../../typings/filter';
import { PeriodFilterValue } from '../../typings/filters';
import { Goal, GoalDecl } from '../../typings/goals';
import {
    ClientFeaturesFragment,
    GdAdPrimaryStatus,
    GdCampaignPrimaryStatus,
    GdOperatorAction,
    GdShowConditionType,
    OperatorFeaturesFragment
} from '../../typings/graphql-grid';
import {
    BannersAction,
    CampaignsAction,
    FeedFiltersAction,
    GridActionData,
    GridActionFeatures,
    GridActionItems,
    GridActionItemType,
    GridColumnDecl,
    GridContextObjectsInfo,
    GridContextTitleData,
    GridData,
    GridGroupedColumn,
    GridSelection,
    GridSelectionInfo,
    GridSourceParams,
    GridTabMenuLinkData,
    GridTooltipDisabledAction,
    GridTotalStatsInfo,
    GridViewType,
    GroupsAction,
    PhrasesAction,
    RetargetingsAction,
    UsersProfileAction
} from '../../typings/grid';
import { BannerViewType, PcodeRenderData } from '../../typings/pcode';
import { ClientFeatures, State } from '../../typings/state';
import { UserProfile } from '../../typings/userProfileRetargeting';
import { getEditableBannerTypes } from '../bannerEdit/banner';
import { selectDealsColumns } from '../deals';
import { selectGridSourceParams } from '../gridSourceParams/gridSourceParams';
import { selectEditableGroupTypes } from '../groupEdit/group/group';
import { selectLogsColumns } from '../log';
import { selectReportsClientColumns } from '../reportsClient';
import { selectReportsDomainColumns } from '../reportsDomain';

import * as keyset from './grid.i18n';

const i18n = i18nFactory(keyset);

export const selectGridSourceParamsView = createSelector(
    selectGridSourceParams,
    (params: GridSourceParams): null | string => {
        if (!params) {
            return null;
        }

        return params.view;
    }
);

export const selectGridAdditionalFilter = createSelector(
    (state: State): AdditionalFiltersDict => state.grid.additionalFilterDict,
    selectGridSourceParams,
    (
        additionalFiltersDict: AdditionalFiltersDict,
        sourceParams?: GridSourceParams
    ): AdditionalFilter | undefined => {
        if (!sourceParams) {
            return undefined;
        }

        const { view } = sourceParams;
        const additionalFilterItems = getAdditionalFilterItems(view);
        const additionalFilterValue = additionalFiltersDict[view];

        return additionalFilterItems.length
            ? {
                  additionalFilterItems,
                  additionalFilterValue
              }
            : undefined;
    }
);

export const selectCalcTypeFilter = createSelector(
    (state: State) => state.grid.campaignsCalcTypeFilter,
    (state: State) => state.commonData.features,
    selectGridSourceParams,
    (
        calcTypeFilterValue: CampaignsCalcTypeFilter,
        features: ClientFeatures,
        sourceParams?: GridSourceParams
    ): CalcTypeFilter | undefined => {
        if (!sourceParams) {
            return undefined;
        }

        const { view } = sourceParams;
        const calcTypeFilterItems = getCalcTypeFilterItems(
            view,
            features.isCpmCampaignsEnabled
        );

        return calcTypeFilterItems.length
            ? {
                  calcTypeFilterItems,
                  calcTypeFilterValue
              }
            : undefined;
    }
);

export const selectOnlyFavoriteCampaigns = createSelector(
    selectGridSourceParamsView,
    (state: State) => state.grid.isOnlyFavoriteCampaignsVisible,
    (view: GridViewType, isOnlyFavoriteCampaignsVisible: boolean) =>
        view === campaigns && isOnlyFavoriteCampaignsVisible
);

export const selectStatPeriodFilterValue = createSelector(
    selectGridSourceParams,
    (sourceParams: GridSourceParams): PeriodFilterValue => {
        let statPeriod: PeriodFilterValue;

        if (sourceParams && sourceParams.statPeriod) {
            statPeriod = {
                dateRangePreset: sourceParams.statPeriod.statPreset,
                dateFrom: sourceParams.statPeriod.statFrom
                    ? moment(sourceParams.statPeriod.statFrom)
                    : undefined,
                dateTo: sourceParams.statPeriod.statTo
                    ? moment(sourceParams.statPeriod.statTo)
                    : undefined
            };
        } else {
            return {
                dateRangePreset: DateRangePreset.last30Days
            };
        }

        const { dateRangePreset, dateFrom, dateTo } = statPeriod;

        if (dateFrom && dateTo) {
            return {
                dateFrom,
                dateTo
            };
        } else {
            return {
                dateRangePreset: dateRangePreset || DateRangePreset.last30Days
            };
        }
    }
);

/* Возвращает минимальную и максимальную даты выбора периода статистики в зависимости от view таблицы */
export const selectGridStatPeriodLimits = createSelector(
    selectGridSourceParamsView,
    (view: string): { minDate?: moment.Moment; maxDate?: moment.Moment } => {
        if (!view) {
            return {};
        }

        switch (view) {
            case campaigns:
            case groups:
            case phrases:
            case banners:
            case retargetings:
            case feedFilters:
            case usersProfile:
                return {
                    minDate: moment().subtract(90, 'days'),
                    maxDate: moment()
                };

            default:
                throw new Error('Unsupported grid view type');
        }
    }
);

export const selectActiveActions = createSelector(
    selectGridSourceParams,
    (state: State) => state.grid.selectionInfo.groups,
    (state: State) => state.grid.gridData.data,
    (state: State) => state.grid.gridActionFeatures,
    (
        sourceParams: GridSourceParams,
        selectionInfo: GridSelection,
        gridData: Array<Group>,
        gridActionFeatures?: GridActionFeatures
    ): GroupItemActionAccess => {
        if (sourceParams.view !== groups) {
            return {
                canAcceptAdsCalloutsModeration: false,
                canRemoderateAdsCallouts: false,
                canBeDeleted: false,
                canAcceptModeration: false,
                canBeSentToRemoderation: false,
                canBeSentToModeration: false,
                canBeSentToModerationByClient: false,
                canBeSentToBS: false,
                canCopy: false
            };
        } else if (gridActionFeatures) {
            const {
                canAcceptAdsCalloutsModerationCount,
                canRemoderateAdsCalloutsCount,
                canBeDeletedAdGroupsCount,
                canAcceptModerationAdGroupsCount,
                canBeSentToModerationAdGroupsCount,
                canBeSentToModerationByClientAdGroupsCount,
                canBeSentToRemoderationAdGroupsCount,
                canBeSentToBSAdGroupsCount,
                canBeCopied
            } = gridActionFeatures;

            return {
                canAcceptAdsCalloutsModeration: isGroupActionAvailable(
                    canAcceptAdsCalloutsModerationCount,
                    'canAcceptAdsCalloutsModeration',
                    selectionInfo,
                    gridData
                ),
                canRemoderateAdsCallouts: isGroupActionAvailable(
                    canRemoderateAdsCalloutsCount,
                    'canRemoderateAdsCallouts',
                    selectionInfo,
                    gridData
                ),
                canAcceptModeration: isGroupActionAvailable(
                    canAcceptModerationAdGroupsCount,
                    'canAcceptModeration',
                    selectionInfo,
                    gridData
                ),
                canBeSentToRemoderation: isGroupActionAvailable(
                    canBeSentToRemoderationAdGroupsCount,
                    'canBeSentToRemoderation',
                    selectionInfo,
                    gridData
                ),
                canBeSentToModeration: isGroupActionAvailable(
                    canBeSentToModerationAdGroupsCount,
                    'canBeSentToModeration',
                    selectionInfo,
                    gridData
                ),
                canBeSentToModerationByClient: isGroupActionAvailable(
                    canBeSentToModerationByClientAdGroupsCount,
                    'canBeSentToModerationByClient',
                    selectionInfo,
                    gridData
                ),
                canBeDeleted: isGroupActionAvailable(
                    canBeDeletedAdGroupsCount,
                    'canBeDeleted',
                    selectionInfo,
                    gridData
                ),
                canBeSentToBS: isGroupActionAvailable(
                    canBeSentToBSAdGroupsCount,
                    'canBeSentToBS',
                    selectionInfo,
                    gridData
                ),
                canCopy: isGroupActionAvailable(
                    canBeCopied,
                    'canCopy',
                    selectionInfo,
                    gridData
                )
            };
        } else {
            if (isSelectedMoreThenInGrid(selectionInfo, gridData)) {
                return {
                    canAcceptAdsCalloutsModeration: false,
                    canRemoderateAdsCallouts: false,
                    canBeDeleted: false,
                    canAcceptModeration: false,
                    canBeSentToRemoderation: false,
                    canBeSentToModeration: false,
                    canBeSentToModerationByClient: false,
                    canBeSentToBS: false,
                    canCopy: false
                };
            }

            return {
                canAcceptAdsCalloutsModeration: isGroupActionAvailable(
                    0,
                    'canAcceptAdsCalloutsModeration',
                    selectionInfo,
                    gridData
                ),
                canRemoderateAdsCallouts: isGroupActionAvailable(
                    0,
                    'canRemoderateAdsCallouts',
                    selectionInfo,
                    gridData
                ),
                canAcceptModeration: isGroupActionAvailable(
                    0,
                    'canAcceptModeration',
                    selectionInfo,
                    gridData
                ),
                canBeSentToModerationByClient: isGroupActionAvailable(
                    0,
                    'canBeSentToModerationByClient',
                    selectionInfo,
                    gridData
                ),
                canBeSentToRemoderation: isGroupActionAvailable(
                    0,
                    'canBeSentToRemoderation',
                    selectionInfo,
                    gridData
                ),
                canBeSentToModeration: isGroupActionAvailable(
                    0,
                    'canBeSentToModeration',
                    selectionInfo,
                    gridData
                ),
                canBeDeleted: isGroupActionAvailable(
                    0,
                    'canBeDeleted',
                    selectionInfo,
                    gridData
                ),
                canBeSentToBS: isGroupActionAvailable(
                    0,
                    'canBeSentToBS',
                    selectionInfo,
                    gridData
                ),
                canCopy: isGroupActionAvailable(
                    0,
                    'canCopy',
                    selectionInfo,
                    gridData
                )
            };
        }
    }
);

/* Возращает объект для формирования меню массовых действий */
export const selectGridItemsActions = createSelector(
    selectGridSourceParams,
    selectActiveActions,
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.accessRights,
    (state: State) => state.grid.selectionInfo,
    (state: State) => state.grid.gridData.data,
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.constants,
    (state: State) => state.commonData.features,
    (state: State) => state.grid.clientInfo,
    (state: State) => state.grid.campaignsCalcTypeFilter,
    (state: State) => state.grid.gridActionFeatures,
    (
        sourceParams: GridSourceParams,
        gridItemActionAccess: GroupItemActionAccess,
        operatorFeauter: OperatorFeaturesFragment,
        accessRights: AccessRights,
        selectionInfo: GridSelectionInfo,
        gridData: Array<GridRow>,
        operatorFeatures: OperatorFeaturesFragment,
        constants: ConstData,
        clientFeatures: ClientFeatures,
        clientInfo: ClientInfo,
        calcTypeFilterValue: CampaignsCalcTypeFilter,
        gridActionFeatures?: GridActionFeatures
    ): GridActionItems => {
        if (!sourceParams || !constants) {
            return [];
        }
        const { view } = sourceParams;

        switch (view) {
            case campaigns:
                return getGridItemsActions(
                    getActionsItemsForCampaignsGrid(
                        selectionInfo[campaigns],
                        gridData,
                        accessRights
                    ),
                    view
                );

            case groups:
                return getGridItemsActions(
                    getActionsItemsForGroupsGrid(
                        selectionInfo[groups],
                        operatorFeatures,
                        gridItemActionAccess,
                        clientFeatures,
                        constants,
                        gridData as Group[],
                        accessRights,
                        calcTypeFilterValue,
                        gridActionFeatures
                    ),
                    view
                );

            case banners:
                return getGridItemsActions(
                    getActionsItemsForBannersGrid(
                        selectionInfo[banners],
                        gridData as Banner[],
                        clientInfo,
                        operatorFeatures,
                        accessRights
                    ),
                    view
                );
            case phrases:
                return getGridItemsActions(
                    getActionsItemsForPhrasesGrid(
                        selectionInfo[phrases],
                        gridData as Phrase[],
                        calcTypeFilterValue
                    ),
                    view
                );

            case feedFilters:
                return getGridItemsActions(
                    getActionsItemsForFeedFiltersGrid(),
                    view
                );

            case retargetings:
                return getGridItemsActions(
                    getActionsItemsForRetargetingsGrid(
                        selectionInfo[retargetings],
                        gridData as Retargeting[]
                    ),
                    view
                );
            case usersProfile:
                return getGridItemsActions(
                    getActionsItemsForUsersProfileGrid(
                        selectionInfo[usersProfile],
                        gridData as UserProfile[]
                    ),
                    view
                );
            default:
                return [];
        }
    }
);

function getActionsItemsForCampaignsGrid(
    selection: GridSelection,
    gridData: Array<GridRow>,
    accessRight?: AccessRights
): Array<GridActionData> {
    let isModerateDisabled = false;
    const canEditCampaigns = accessRight && accessRight.canCreateCampaign; // todo DIRECT-86710
    const availableActions = (accessRight && accessRight.actions) || [];

    if (selection.length === 1) {
        const campaign = gridData.find(
            el => el.id === selection[0]
        ) as Campaign;

        // отправка на модерацию доступна только для черновиков
        if (
            campaign &&
            (campaign.statusInfo.type !== GdCampaignPrimaryStatus.DRAFT ||
                !campaign.hasBanners)
        ) {
            isModerateDisabled = true;
        }
    } else {
        isModerateDisabled = true;
    }

    const сanPayByCash = accessRight && accessRight.canPayByCash;

    return [
        {
            action: CampaignsAction.resume,
            type: GridActionItemType.action,
            hidden: !canEditCampaigns
        },
        {
            action: CampaignsAction.stop,
            type: GridActionItemType.action,
            hidden: !canEditCampaigns
        },
        {
            action: CampaignsAction.archive,
            type: GridActionItemType.action
        },
        {
            action: CampaignsAction.unarchive,
            type: GridActionItemType.action
        },
        {
            action: CampaignsAction.remoderate,
            type: GridActionItemType.action,
            hidden: !availableActions.includes(
                GdOperatorAction.REMODERATE_CAMPAIGN
            )
        },
        {
            action: CampaignsAction.delete,
            type: GridActionItemType.action,
            hidden: !canEditCampaigns
        },
        {
            action: CampaignsAction.copy,
            type: GridActionItemType.action,
            hidden: !canEditCampaigns
        },
        {
            action: CampaignsAction.goToStatistics,
            type: GridActionItemType.nav
        },
        {
            action: CampaignsAction.goToLogs,
            type: GridActionItemType.nav,
            hidden: accessRight && accessRight.canViewUserLogs === false
        },
        {
            action: CampaignsAction.goToCampaignModerationOffer,
            type: GridActionItemType.nav,
            disabled: !canEditCampaigns || isModerateDisabled
        },
        {
            action: CampaignsAction.addToFavorites,
            type: GridActionItemType.action
        },
        {
            action: CampaignsAction.removeFromFavorites,
            type: GridActionItemType.action
        },
        {
            action: CampaignsAction.payByCash,
            type: GridActionItemType.nav,
            hidden: !сanPayByCash
        }
    ];
}

function isEditGroupsEnabled(
    selectionInfo: GridSelection,
    constants: ConstData,
    gridData: Group[],
    operatorFeatures: OperatorFeaturesFragment,
    accessRights: AccessRights
): GridTooltipDisabledAction {
    if (!operatorFeatures.isAdGroupUpdateAllowed) {
        return {
            enabled: false
        };
    }

    const editableTypes = selectEditableGroupTypes(
        operatorFeatures,
        accessRights
    );
    const isSelectedOneOrMoreEditableCampaignsType =
        gridData
            .filter(Boolean)
            .filter(
                item =>
                    selectionInfo.includes(item.id) &&
                    editableTypes.has(item.type) &&
                    item.access.canEdit
            ).length > 0;

    if (!isSelectedOneOrMoreEditableCampaignsType) {
        return {
            enabled: false
        };
    }

    if (selectionInfo.length > constants.maxGroupsNumberForEditOperation) {
        return {
            enabled: false,
            tooltipText: i18n('groups-max-selected', {
                maxCount: constants.maxGroupsNumberForEditOperation
            })
        };
    }

    return {
        enabled: true
    };
}

function getActionsItemsForGroupsGrid(
    selectionInfo: GridSelection,
    operatorFeatures: OperatorFeaturesFragment,
    itemActionAccess: GroupItemActionAccess,
    features: ClientFeatures,
    constants: ConstData,
    gridData: Group[],
    accessRights: AccessRights,
    calcTypeFilterValue: CampaignsCalcTypeFilter,
    gridActionFeatures?: GridActionFeatures
): Array<GridActionData> {
    const areSeveralItemsSelected = selectionInfo.length !== 1;
    const { enabled, tooltipText } = isEditGroupsEnabled(
        selectionInfo,
        constants,
        gridData,
        operatorFeatures,
        accessRights
    );
    const canManageLibraryMinusPhrase = Boolean(
        accessRights.canManageLibMinusWords
    );
    const accessActions = accessRights.actions || [];
    const isOverMaxSelectionLimit =
        selectionInfo.length >
        constants.adGroupsConstants.maxAdGroupsCountPerUpdate;
    const selectItems = gridData.filter(item =>
        selectionInfo.includes(item.id)
    );

    const isOnlyTextCampaignTypeSelected = selectItems.every(
        item => item.campaign.type === CampaignType.TEXT
    );
    const canCpmCopy = selectItems.every(
        item =>
            item.campaign.type === CampaignType.CPM_BANNER &&
            (item.type === GroupType.CPM_VIDEO ||
                item.type === GroupType.CPM_BANNER)
    );
    const isMediaCampaignTypeSelected = selectItems.every(
        item => item.campaign.type === CampaignType.CPM_BANNER
    );
    const isMediaGroupWithoutKeywords = selectItems.every(
        item =>
            item.campaign.type === CampaignType.CPM_BANNER &&
            item.availableShowConditionTypes[0] !== GdShowConditionType.KEYWORD
    );

    return compact([
        {
            action: GroupsAction.resume,
            type: GridActionItemType.action
        },
        {
            action: GroupsAction.stop,
            type: GridActionItemType.action
        },
        {
            action: GroupsAction.delete,
            type: GridActionItemType.action,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canBeDeletedAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.delete,
                          isSelectionValid: itemActionAccess.canBeDeleted,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            disabled: !itemActionAccess.canBeDeleted || isOverMaxSelectionLimit
        },
        {
            action: GroupsAction.sendBK,
            type: GridActionItemType.action,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canBeSentToBSAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.sendBK,
                          isSelectionValid: itemActionAccess.canBeSentToBS,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            disabled:
                !itemActionAccess.canBeSentToBS || isOverMaxSelectionLimit,
            hidden: !accessActions.includes(GdOperatorAction.SEND_TO_BS)
        },
        {
            action: GroupsAction.archive,
            type: GridActionItemType.action
        },
        {
            action: GroupsAction.copy,
            type: GridActionItemType.action,
            disabled:
                !(isOnlyTextCampaignTypeSelected || canCpmCopy) ||
                isOverMaxSelectionLimit ||
                !itemActionAccess.canCopy,
            tooltipText: getTextForItemsGroupsActionTooltipText({
                action: GroupsAction.copy,
                isSelectionValid: itemActionAccess.canCopy,
                isOnlyTextCampaignTypeSelected,
                canCpmCopy,
                calcTypeFilterValue,
                isOverMaxSelection: isOverMaxSelectionLimit
            })
        },
        {
            action: GroupsAction.openMinusPhraseSelector,
            type: GridActionItemType.action,
            hidden:
                !features.isMinusWordsLibEnabled || isMediaCampaignTypeSelected,
            disabled:
                !isOnlyTextCampaignTypeSelected ||
                isOverMaxSelectionLimit ||
                !canManageLibraryMinusPhrase,
            tooltipText: getTextForItemsGroupsActionTooltipText({
                action: GroupsAction.openMinusPhraseSelector,
                isOnlyTextCampaignTypeSelected: isOnlyTextCampaignTypeSelected,
                canManageLibraryMinusPhrase: canManageLibraryMinusPhrase,
                isOverMaxSelection: isOverMaxSelectionLimit,
                maxSelectionLength:
                    constants.adGroupsConstants.maxAdGroupsCountPerUpdate
            })
        },
        {
            action: GroupsAction.unarchive,
            type: GridActionItemType.action
        },
        {
            action: GroupsAction.remoderateBannersAdditions,
            type: GridActionItemType.action,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canRemoderateAdsCallouts,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canRemoderateAdsCalloutsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.remoderateBannersAdditions,
                          isSelectionValid:
                              itemActionAccess.canRemoderateAdsCallouts,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            hidden: !accessActions.includes(
                GdOperatorAction.REMODERATE_ADS_CALLOUTS
            )
        },
        {
            action: GroupsAction.moderateAcceptBannersAdditions,
            type: GridActionItemType.action,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canAcceptAdsCalloutsModeration,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canAcceptAdsCalloutsModerationCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.moderateAcceptBannersAdditions,
                          isSelectionValid:
                              itemActionAccess.canAcceptAdsCalloutsModeration,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            hidden: !accessActions.includes(
                GdOperatorAction.ACCEPT_ADS_CALLOUTS_MODERATION
            )
        },
        {
            action: GroupsAction.admSendMD,
            type: GridActionItemType.action,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canBeSentToRemoderation,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canBeSentToRemoderationAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.admSendMD,
                          isSelectionValid:
                              itemActionAccess.canBeSentToRemoderation,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            hidden: !accessActions.includes(
                GdOperatorAction.SEND_TO_REMODERATION
            )
        },
        {
            action: GroupsAction.sendModerateByAgency,
            type: GridActionItemType.action,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canBeSentToModeration,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canBeSentToModerationAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.sendModerateByAgency,
                          isSelectionValid:
                              itemActionAccess.canBeSentToModeration,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            hidden: !accessActions.includes(GdOperatorAction.SEND_TO_MODERATION)
        },
        {
            action: GroupsAction.sendModerateByClient,
            type: GridActionItemType.action,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canBeSentToModerationByClient,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canBeSentToModerationByClientAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.sendModerateByClient,
                          isSelectionValid:
                              itemActionAccess.canBeSentToModerationByClient,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            hidden: !accessActions.includes(
                GdOperatorAction.SEND_TO_MODERATION_BY_CLIENT
            )
        },
        {
            action: GroupsAction.moderateAccept,
            type: GridActionItemType.action,
            tooltipText:
                gridActionFeatures &&
                gridActionFeatures.canAcceptModerationAdGroupsCount
                    ? getTextForItemsGroupsActionTooltipText({
                          action: GroupsAction.moderateAccept,
                          isSelectionValid:
                              itemActionAccess.canAcceptModeration,
                          isOverMaxSelection: isOverMaxSelectionLimit
                      })
                    : undefined,
            disabled:
                isOverMaxSelectionLimit ||
                !itemActionAccess.canAcceptModeration,
            hidden: !accessActions.includes(GdOperatorAction.ACCEPT_MODERATION)
        },
        operatorFeatures.isAdGroupUpdateAllowed && {
            action: GroupsAction.editGroups,
            type: GridActionItemType.nav,
            disabled: !enabled,
            tooltipText
        },
        {
            action: GroupsAction.goToStatistics,
            type: GridActionItemType.nav
        },
        {
            action: GroupsAction.openPhrasesSelectorWizard,
            type: GridActionItemType.action,
            disabled: areSeveralItemsSelected || isMediaGroupWithoutKeywords
        }
    ]);
}

function isActionEditBannersEnabled(
    selectionInfo: GridSelection,
    hashBannersData: HashBannersData,
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): GridTooltipDisabledAction {
    const editableTypes = getEditableBannerTypes(features, accessRights);
    // если выбрали ничего или больше 50 штук - нельзя
    if (selectionInfo.length === 0 || selectionInfo.length > 50) {
        return {
            enabled: false,
            tooltipText: i18n('banners-max-selected', {
                maxCount: 50
            })
        };
    }

    // ищем хотя бы один баннер, который можно редактировать и который входит в состав
    // разрешенных для редактирования баннеров
    const isContainsEditableTypesBanners = selectionInfo.some(id => {
        const banner = hashBannersData[id];

        return Boolean(
            banner &&
                banner.isEditAllow &&
                banner.status.type !== GdAdPrimaryStatus.ARCHIVED &&
                editableTypes.includes(banner.bannerType)
        );
    });

    return {
        enabled: isContainsEditableTypesBanners
    };
}

function isActionDeleteBannerDisabled(
    selectionInfo: GridSelection,
    hashBannersData: HashBannersData
): boolean {
    const hasAvailableBanner = selectionInfo.some(id =>
        Boolean(hashBannersData[id])
    );

    // нет доступных объявлений - блокируем кнопку
    return !hasAvailableBanner;
}

function isActionEditBannerPriceDisabled(
    selectionInfo: GridSelection,
    hashBannersData: HashBannersData
): boolean {
    const hasAvailableBanner = selectionInfo.some(id => {
        const banner = hashBannersData[id];
        return Boolean(banner && isBannerPriceEditable(banner));
    });

    // нет доступных объявлений - блокируем кнопку
    return !hasAvailableBanner;
}

function isActionEditBannerOrganizationDisabled(
    selectionInfo: GridSelection,
    hashBannersData: HashBannersData
): boolean {
    const hasAvailableBanner = selectionInfo.some(id => {
        const banner = hashBannersData[id];
        return Boolean(banner && isBannerOrganizationEditable(banner));
    });

    // нет доступных объявлений - блокируем кнопку
    return !hasAvailableBanner;
}

type HashBannersData = Record<string, Banner>;

function getActionsItemsForBannersGrid(
    selectionInfo: GridSelection,
    bannersData: Banner[],
    clientInfo: ClientInfo,
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): Array<GridActionData> {
    const editableTypes = getEditableBannerTypes(features, accessRights);

    const hashBannersData: HashBannersData = bannersData.reduce(
        (acc: HashBannersData, banner): HashBannersData => {
            if (banner !== undefined) {
                acc[banner.id] = banner;
            }

            return acc;
        },
        {}
    );
    const { enabled, tooltipText } = isActionEditBannersEnabled(
        selectionInfo,
        hashBannersData,
        features,
        accessRights
    );

    const isActionEditPriceDisabled = isActionEditBannerPriceDisabled(
        selectionInfo,
        hashBannersData
    );

    const isActionDeleteDisabled = isActionDeleteBannerDisabled(
        selectionInfo,
        hashBannersData
    );

    const isActionEditOrganizationDisabled = isActionEditBannerOrganizationDisabled(
        selectionInfo,
        hashBannersData
    );

    return compact([
        {
            action: BannersAction.archive,
            type: GridActionItemType.action
        },
        {
            action: BannersAction.unarchive,
            type: GridActionItemType.action
        },
        {
            action: BannersAction.stop,
            type: GridActionItemType.action
        },
        {
            action: BannersAction.resume,
            type: GridActionItemType.action
        },
        {
            action: BannersAction.delete,
            type: GridActionItemType.action,
            disabled: isActionDeleteDisabled
        },
        accessRights.canEditInternalAds
            ? {
                  action: BannersAction.editBannersInternal,
                  type: GridActionItemType.nav,
                  disabled: !enabled,
                  tooltipText
              }
            : editableTypes.length && {
                  action: BannersAction.editBanners,
                  type: GridActionItemType.nav,
                  disabled: !enabled,
                  tooltipText
              },
        clientInfo.isBannerPricesOnGridEnabled && {
            action: BannersAction.editPrices,
            type: GridActionItemType.action,
            disabled: isActionEditPriceDisabled
        },
        clientInfo.isTycoonOrganizationsEnabled && {
            action: BannersAction.editYellowPagesData,
            type: GridActionItemType.action,
            disabled: isActionEditOrganizationDisabled
        }
    ]);
}

function isActionEditCommonPricePhraseEnabled(
    selectionInfo: GridSelection,
    gridDataMap: (Phrase | undefined)[]
): boolean {
    return selectionInfo.some(phraseId => {
        const phrase = gridDataMap.find(
            element => !!element && element.id === phraseId
        );

        if (!phrase) {
            return false;
        }

        const {
            priceContextEditable,
            pokazometerData,
            group: {
                campaign: {
                    strategy: { isAutoBudget }
                }
            }
        } = phrase;

        return Boolean(
            priceContextEditable && pokazometerData && !isAutoBudget
        );
    });
}

function getActionsItemsForPhrasesGrid(
    selectionInfo: GridSelection,
    gridData: (Phrase | undefined)[],
    calcTypeFilterValue: CampaignsCalcTypeFilter
): Array<GridActionData> {
    const hasRelevanceMatch = selectionInfo.some((phraseId: string) => {
        const phrase = gridData.find(value =>
            Boolean(value && value.id === phraseId)
        );
        return phrase
            ? phrase.type === GdShowConditionType.RELEVANCE_MATCH
            : false;
    });
    const isCommonPriceEnabled = isActionEditCommonPricePhraseEnabled(
        selectionInfo,
        gridData
    );

    return compact([
        {
            action: PhrasesAction.delete,
            type: GridActionItemType.action
        },
        {
            action: PhrasesAction.enable,
            type: GridActionItemType.action
        },
        {
            action: PhrasesAction.disable,
            type: GridActionItemType.action
        },
        {
            action: PhrasesAction.openSearchAndReplaceInspector,
            type: GridActionItemType.action,
            disabled: hasRelevanceMatch,
            tooltipText: i18n(
                'search-and-replace-forbidden-for-relevance-match'
            )
        },
        calcTypeFilterValue === CampaignsCalcTypeFilter.CPC && {
            action: PhrasesAction.pricesWizard,
            type: GridActionItemType.action
        },
        calcTypeFilterValue === CampaignsCalcTypeFilter.CPM && {
            action: PhrasesAction.setPhrasesCommonPriceEditor,
            type: GridActionItemType.action,
            disabled: !isCommonPriceEnabled
        }
    ]);
}

function getActionsItemsForFeedFiltersGrid(): Array<GridActionData> {
    return [
        {
            action: FeedFiltersAction.changeRate,
            type: GridActionItemType.action
        },
        {
            action: FeedFiltersAction.changeAutobudget,
            type: GridActionItemType.action
        },
        {
            action: FeedFiltersAction.disable,
            type: GridActionItemType.action
        },
        {
            action: FeedFiltersAction.enable,
            type: GridActionItemType.action
        },
        {
            action: FeedFiltersAction.delete,
            type: GridActionItemType.action
        }
    ];
}

function isActionEditRetargetingsEnabled(
    selectionInfo: GridSelection,
    gridDataMap: Record<string, Retargeting | UserProfile>
): boolean {
    return selectionInfo.some(id => {
        const retargeting = gridDataMap[id];
        return Boolean(retargeting && retargeting.isEditable);
    });
}

function isSomePriceContextEnabled(
    selectionInfo: GridSelection,
    gridDataMap: Record<string, Retargeting | UserProfile>
): boolean {
    return selectionInfo.some(id => {
        const retargeting = gridDataMap[id];
        return Boolean(retargeting && retargeting.isPriceContextEnabled);
    });
}

function getActionsItemsForRetargetingsGrid(
    selectionInfo: GridSelection,
    gridData: Retargeting[]
): Array<GridActionData> {
    const gridDataMap = gridData.reduce((acc, retargeting) => {
        acc[retargeting.id] = retargeting;
        return acc;
    }, {});

    const isEnabled = isActionEditRetargetingsEnabled(
        selectionInfo,
        gridDataMap
    );
    const isPriceContextEnabled = isSomePriceContextEnabled(
        selectionInfo,
        gridDataMap
    );

    return [
        {
            action: RetargetingsAction.setRetargetingCommonPriceEditor,
            type: GridActionItemType.action,
            disabled: !(isEnabled && isPriceContextEnabled)
        },
        {
            action: RetargetingsAction.resume,
            type: GridActionItemType.action,
            disabled: !isEnabled
        },
        {
            action: RetargetingsAction.suspend,
            type: GridActionItemType.action,
            disabled: !isEnabled
        },
        {
            action: RetargetingsAction.delete,
            type: GridActionItemType.action,
            disabled: !isEnabled
        }
    ];
}

function getActionsItemsForUsersProfileGrid(
    selectionInfo: GridSelection,
    gridData: UserProfile[]
): Array<GridActionData> {
    const gridDataMap = gridData.reduce((acc, retargeting) => {
        acc[retargeting.id] = retargeting;
        return acc;
    }, {});
    const isEnabled = isActionEditRetargetingsEnabled(
        selectionInfo,
        gridDataMap
    );

    const isPriceContextEnabled = isSomePriceContextEnabled(
        selectionInfo,
        gridDataMap
    );

    return [
        {
            action: UsersProfileAction.setUsersProfileCommonPriceEditor,
            type: GridActionItemType.action,
            disabled: !isEnabled || !isPriceContextEnabled
        }
    ];
}

export const selectContextObjectsInfo = createSelector(
    (state: State) => state.grid.additionalFilterDict,
    selectGridSourceParams,
    (
        additionalFiltersDict: AdditionalFiltersDict,
        params?: GridSourceParams
    ): GridContextObjectsInfo | null => {
        if (!params) {
            return null;
        }

        const { context, view, campaignsIds, groupsIds, login } = params;

        const additionalFilter = additionalFiltersDict[view];

        const res: GridContextObjectsInfo = {
            clients: {
                titleData: getCampaignsTitleData(additionalFilter),
                location: GridScreenLinkBuilder.getContextTypeRelevantUrl(
                    clients,
                    campaigns,
                    {
                        login
                    },
                    params
                )
            }
        };

        if (context === campaigns || context === groups) {
            const parentCampaignsIds = campaignsIds || [];

            res.campaigns = {
                titleData: getTitleDataForView(groups, additionalFilter),
                location: GridScreenLinkBuilder.getContextTypeRelevantUrl(
                    campaigns,
                    groups,
                    {
                        login,
                        campaignsIds: parentCampaignsIds,
                        contextIds: parentCampaignsIds
                    },
                    params
                )
            };
        }

        if (context === groups) {
            // todo: убрать поиск после DIRECT-75200

            res.groups = {
                titleData: getTitleDataForView(view, additionalFilter),
                location: GridScreenLinkBuilder.getContextTypeRelevantUrl(
                    groups,
                    view,
                    {
                        login,
                        groupsIds
                    },
                    params
                )
            };
        }

        return res;
    }
);

export const selectGridAlertMessage = createSelector(
    selectGridSourceParams,
    (params?: GridSourceParams): string | undefined => {
        if (!params || !params.messageType) {
            return undefined;
        } else {
            return getGridAlertMessage(params.messageType);
        }
    }
);

export const selectGridMessageType = createSelector(
    selectGridSourceParams,
    (params?: GridSourceParams): AlertType => {
        if (!params || !params.messageType) {
            return AlertType.info;
        } else {
            return getGridAlertMessageType(params.messageType);
        }
    }
);

export const selectGridScreenTitle = createSelector(
    selectContextObjectsInfo,
    selectGridSourceParams,
    (
        viewInfo?: GridContextObjectsInfo,
        params?: GridSourceParams
    ): GridContextTitleData => {
        if (!viewInfo || !params) {
            return {
                text: ''
            };
        }

        const viewContextInfo = viewInfo[params.context];

        if (!viewContextInfo) {
            throw Error(`Для view ${params.context} не определен заголовок`);
        }

        return viewContextInfo.titleData;
    }
);

export const selectEntitiesTabNavLinks = createSelector(
    (state: State) => state.grid.selectionInfo,
    selectGridSourceParams,
    (state: State) => state.grid.filterDict,
    (
        selectionInfo: GridSelectionInfo,
        params: GridSourceParams,
        filterDict?: FilterGridViewDict
    ): Array<GridTabMenuLinkData> => {
        const { view, login, statPeriod } = params;
        const hasSelectedCampaigns = selectionInfo.campaigns.length !== 0;
        const filter = filterDict || {};

        return [
            {
                title: i18n('campaigns-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.campaigns.length),
                location: GridScreenLinkBuilder.getContextTypeRelevantUrl(
                    clients,
                    campaigns,
                    {
                        login,
                        filter: filter[campaigns],
                        ...statPeriod
                    },
                    params
                ),
                isActive: view === campaigns
            },
            {
                title: i18n('groups-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.groups.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          campaigns,
                          groups,
                          {
                              login,
                              contextIds: selectionInfo.campaigns,
                              filter: filter[groups],
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === groups
            },
            {
                title: i18n('banners-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.banners.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          groups,
                          banners,
                          {
                              login,
                              contextIds: selectionInfo.groups,
                              campaignsIds: selectionInfo.campaigns,
                              filter: filter[banners],
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === banners
            }
        ];
    }
);

export const selectShowConditionsTabNavLinks = createSelector(
    (state: State) => state.grid.selectionInfo,
    (state: State) => state.commonData.accessRights,
    (state: State) => state.commonData.dataFetched,
    (state: State) => state.grid.filterDict,
    (state: State) => state.grid.campaignsCalcTypeFilter,
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.features,
    selectGridSourceParams,
    (
        selectionInfo: GridSelectionInfo,
        access: AccessRights,
        dataFetched: boolean,
        filterDict?: FilterGridViewDict,
        campaignsCalcTypeFilter?: CampaignsCalcTypeFilter,
        operatorFeatures?: OperatorFeaturesFragment,
        clientFeatures?: ClientFeaturesFragment,
        params?: GridSourceParams
    ): Array<GridTabMenuLinkData> => {
        if (!params) {
            return [];
        }

        const { view, login, statPeriod } = params;
        const hasSelectedCampaigns = selectionInfo.campaigns.length !== 0;
        const filter = filterDict || {};

        const tabs = [];
        if (dataFetched && !access.canEditInternalAds) {
            tabs.push({
                title: i18n('phrases-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.phrases.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          groups,
                          phrases,
                          {
                              login,
                              contextIds: selectionInfo.groups,
                              campaignsIds: selectionInfo.campaigns,
                              filter: filter[phrases],
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === phrases
            });
        }

        if (
            dataFetched &&
            operatorFeatures &&
            operatorFeatures.isPerformanceAdEditAllowed &&
            campaignsCalcTypeFilter !== CampaignsCalcTypeFilter.CPM &&
            !access.canEditInternalAds
        ) {
            tabs.push({
                title: i18n('feed-filters-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.feedFilters.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          groups,
                          feedFilters,
                          {
                              login,
                              contextIds: selectionInfo.groups,
                              campaignsIds: selectionInfo.campaigns,
                              filter: filter[feedFilters],
                              campaignsCalcTypeFilter:
                                  CampaignsCalcTypeFilter.CPC,
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === feedFilters
            });
        }

        if (
            dataFetched &&
            operatorFeatures &&
            operatorFeatures.isRetargetingGridEnabled &&
            campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPC &&
            !access.canEditInternalAds
        ) {
            tabs.push({
                title: i18n('retargetings-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.retargetings.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          groups,
                          retargetings,
                          {
                              login,
                              contextIds: selectionInfo.groups,
                              campaignsIds: selectionInfo.campaigns,
                              filter: filter[retargetings],
                              campaignsCalcTypeFilter:
                                  CampaignsCalcTypeFilter.CPC,
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === retargetings
            });
        }
        if (
            dataFetched &&
            campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM &&
            !access.canEditInternalAds
        ) {
            tabs.push({
                title: i18n('users-profile-tab-name'),
                hint: getHintForTabMenuLink(selectionInfo.usersProfile.length),
                location: hasSelectedCampaigns
                    ? GridScreenLinkBuilder.getContextTypeRelevantUrl(
                          groups,
                          usersProfile,
                          {
                              login,
                              contextIds: selectionInfo.groups,
                              campaignsIds: selectionInfo.campaigns,
                              filter: filter[usersProfile],
                              campaignsCalcTypeFilter:
                                  CampaignsCalcTypeFilter.CPM,
                              ...statPeriod
                          },
                          params
                      )
                    : undefined,
                tooltip: hasSelectedCampaigns
                    ? undefined
                    : i18n('select-campaigns'),
                isActive: view === usersProfile
            });
        }

        return tabs;
    }
);

export const selectClientInfo = createSelector(
    (state: State) => state.grid.clientInfo,
    (res: ClientInfo): ClientInfo => res
);

export const selectGridFilterContainers = createSelector(
    selectClientInfo,
    (state: State) => state.goalsData.goals,
    (state: State) => state.commonData.features.isGoalsStatEnabled,
    (state: State) => state.commonData.features.isCpmCampaignsEnabled,
    (state: State) => state.commonData.features.isBidCorrectionSearchEnabled,
    (state: State) => state.grid.campaignsCalcTypeFilter,
    selectGridSourceParams,
    (
        clientInfo: ClientInfo,
        goals: Record<string, Goal>,
        isGoalsStatEnabled: boolean,
        isCpmCampaignsEnabled: boolean,
        isBidCorrectionSearchEnabled?: boolean,
        campaignsCalcTypeFilter?: CampaignsCalcTypeFilter,
        params?: GridSourceParams
    ): CriteriaContainer[] => {
        if (!params) {
            return [];
        }

        const hasOnlyCpmCampaigns =
            isCpmCampaignsEnabled &&
            campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM;

        return getFilterCriteriaContainersByView(
            params.view,
            clientInfo,
            hasOnlyCpmCampaigns,
            isBidCorrectionSearchEnabled,
            goals,
            isGoalsStatEnabled
        );
    }
);

export const selectGridFilterCriterias = createSelector(
    selectGridFilterContainers,
    (filterContainers: CriteriaContainer[]) => {
        return flattenContainer(filterContainers);
    }
);

export const selectGridFilterExpressions = createSelector(
    selectGridFilterCriterias,
    selectGridSourceParams,
    (
        criteria: Criteria[],
        sourceParams?: GridSourceParams
    ): FilterPartialExpression[] => {
        if (!sourceParams || !sourceParams.filter) {
            return [];
        }

        // сопоставление expression, полученных из URL на список дополнительных параметров (целей)
        return sourceParams.filter.map(expressionItem => {
            const additionalFieldItem = findAdditionalCriteriaByExpression(
                expressionItem,
                criteria
            );

            if (!additionalFieldItem) {
                return expressionItem;
            }

            return {
                ...expressionItem,
                additionalField: additionalFieldItem
            };
        });
    }
);

export const selectEditedEntity = createSelector(
    (state: State) => state.grid.editedEntity,
    identity
);

export const selectPhrasesCurrency = createSelector(
    selectGridSourceParams,
    (state: State) => state.grid.gridData.data,
    (sourceParams: GridSourceParams | undefined, phrasesRows: Phrase[]) => {
        if (!sourceParams || sourceParams.view !== phrases) {
            return undefined;
        }
        return phrasesRows.length >= 1
            ? phrasesRows[0].group.campaign.currency
            : undefined;
    }
);

export const selectPricesWizardContextData = createSelector(
    selectPhrasesCurrency,
    (state: State) => state.grid.pricesWizardData,
    (state: State) => state.commonData.constants,
    (currency, pricesWizardData, constants?: ConstData) => {
        if (currency === undefined) {
            return undefined;
        }

        const { contextData } = pricesWizardData;

        if (
            constants !== undefined &&
            currency !== undefined &&
            contextData !== undefined
        ) {
            const { currencyConstants } = constants;

            return {
                currency,
                maxPrice: currencyConstants[currency].maxPrice,
                minPrice: currencyConstants[currency].minPrice,
                defaultPrice: currencyConstants[currency].bigRate,
                ...PricesWizardHelpers.getPricesWizardInspectorsData(
                    contextData
                )
            };
        } else {
            return undefined;
        }
    }
);

export const selectGridBannerTypesData = createSelector(
    (state: State) => state.bannerCreativePreviewPopup.pcodeRenderData,
    (data?: PcodeRenderData) => {
        return data
            ? bannerPreviewSizes.map(size => ({
                  type: BannerViewType.adaptive0418,
                  size,
                  ads: data.ads
              }))
            : [];
    }
);

export const selectSortLinkUrlGetter = createSelector(
    (state: State) => state.router.pathname,
    // Добавление селектора решает проблему https://st.yandex-team.ru/DIRECT-99515
    (state: State) => state.router.search,
    pathname =>
        memoize(
            GridScreenLinkBuilder.getSortLinkUrl.bind(undefined, pathname),
            ({ key, direction, goalId }) => `${key}${direction}${goalId}`
        )
);

export const getGridColumns = (
    constants: ConstData | undefined,
    clientId: string | undefined,
    wallet: WalletData,
    tld: Tld,
    operator: OperatorData,
    clientInfo: ClientInfo,
    columnsSettings: ColumnsSettingsViewDict,
    sourceParams: GridSourceParams | undefined,
    isCompactViewEnabled: boolean,
    clientFeatures: ClientFeatures,
    campaignsCalcTypeFilter?: CampaignsCalcTypeFilter,
    goalsSettings?: GoalDecl[],
    isColumnsSettingsEditorLocation?: boolean
): Record<
    | campaigns
    | groups
    | banners
    | phrases
    | feedFilters
    | retargetings
    | usersProfile,
    Array<GridColumnDecl<GridRow>>
> => {
    const {
        isGoalsStatEnabled = false,
        isCpmCampaignsEnabled = false,
        isBidCorrectionSearchEnabled = false
    } = clientFeatures;
    const { walletBudget, walletActions } = wallet;
    const { workCurrency, countryRegionId } = clientInfo;
    const currentLang = LangUtils.getCurrentLang();
    const { groupsIds = [], login: clientLogin = '' } = sourceParams || {};
    const goalIds = getActiveItemsIds(goalsSettings) || [];
    const hasSelectedGoals = Boolean(goalIds.length);
    const hasSelectedGoalsAtColumnsSettingsEditor = goalsSettings
        ? goalsSettings.some(goalSetting => !Boolean(goalSetting.hidden))
        : undefined;

    const columnsOptions = {
        hasEcommerce: clientInfo.hasEcommerce,
        hasMetrikaCounters: clientInfo.hasMetrikaCounters,
        hasCampaignsWithCPAStrategy: clientInfo.hasCampaignsWithCPAStrategy,
        isWalletEnabled: clientInfo.isWalletEnabled,
        isBrandLiftAllowed: clientInfo.isBrandLiftAllowed,
        workCurrency: workCurrency,
        clientRegion: countryRegionId,
        hasOnlyCpmCampaigns:
            isCpmCampaignsEnabled &&
            campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM,
        operator,
        tld,
        currentLang
    };

    const { hasOnlyCpmCampaigns } = columnsOptions;

    return {
        [campaigns]: getCampaignColumns(
            columnsOptions,
            columnsSettings,
            walletActions,
            workCurrency,
            isCompactViewEnabled,
            isGoalsStatEnabled,
            hasSelectedGoals,
            constants,
            clientLogin,
            walletBudget,
            hasSelectedGoalsAtColumnsSettingsEditor,
            isColumnsSettingsEditorLocation,
            isBidCorrectionSearchEnabled
        ),
        [groups]: getGroupColumns(
            columnsOptions,
            columnsSettings,
            workCurrency,
            isCompactViewEnabled,
            isGoalsStatEnabled,
            hasSelectedGoals,
            clientLogin,
            hasSelectedGoalsAtColumnsSettingsEditor,
            isColumnsSettingsEditorLocation
        ),
        [phrases]: getPhraseColumns(
            {
                ...columnsOptions,
                isSelectedSeveralGroups:
                    groupsIds === undefined || groupsIds.length !== 1
            },
            columnsSettings,
            workCurrency,
            isCompactViewEnabled,
            isGoalsStatEnabled,
            hasSelectedGoals,
            hasOnlyCpmCampaigns,
            constants,
            clientLogin,
            hasSelectedGoalsAtColumnsSettingsEditor,
            isColumnsSettingsEditorLocation
        ),
        [banners]: getBannerColumns(
            {
                ...columnsOptions,
                isBannerPricesOnGridEnabled:
                    clientInfo.isBannerPricesOnGridEnabled || false,
                isSelectedSeveralGroups:
                    groupsIds === undefined || groupsIds.length !== 1
            },
            columnsSettings,
            workCurrency,
            isCompactViewEnabled,
            isGoalsStatEnabled,
            hasSelectedGoals,
            hasSelectedGoalsAtColumnsSettingsEditor,
            isColumnsSettingsEditorLocation,
            constants,
            clientId
        ),
        [retargetings]: getRetargetingColumns(
            columnsOptions,
            columnsSettings,
            workCurrency,
            isCompactViewEnabled,
            isGoalsStatEnabled,
            hasSelectedGoals,
            constants,
            clientLogin,
            hasSelectedGoalsAtColumnsSettingsEditor,
            isColumnsSettingsEditorLocation
        ),
        [feedFilters]: getFeedFilterColumns(
            columnsOptions,
            columnsSettings,
            workCurrency,
            isCompactViewEnabled
        ),
        [usersProfile]: getUsersProfileColumns(
            columnsOptions,
            columnsSettings,
            workCurrency,
            isCompactViewEnabled,
            constants
        )
    };
};

export const selectGridColumns = createSelector(
    (state: State) => state.commonData.constants,
    (state: State) => state.commonData.client.ClientID,
    (state: State) => state.commonData.wallet,
    (state: State) => state.commonData.tld,
    (state: State) => state.commonData.operator,
    (state: State) => state.grid.clientInfo,
    (state: State) => state.grid.columnsSettings,
    selectGridSourceParams,
    (state: State) => state.grid.isCompactViewEnabled,
    (state: State) => state.commonData.features,
    (state: State) => state.grid.campaignsCalcTypeFilter,
    getGridColumns
);

export const selectColumns = createSelector(
    selectGridColumns,
    selectLogsColumns,
    selectReportsClientColumns,
    selectReportsDomainColumns,
    selectDealsColumns,
    (
        gridColumns,
        logsColumns,
        reportsClientColumns,
        reportsDomainColumns,
        dealsColumns
    ): Record<
        | campaigns
        | groups
        | banners
        | retargetings
        | phrases
        | logs
        | deals
        | reportsClient
        | reportsDomain
        | feedFilters
        | usersProfile,
        Array<GridColumnDecl<GridRow>>
    > => {
        return {
            [campaigns]: gridColumns[campaigns],
            [groups]: gridColumns[groups],
            [phrases]: gridColumns[phrases],
            [banners]: gridColumns[banners],
            [retargetings]: gridColumns[retargetings],
            [reportsClient]: reportsClientColumns,
            [reportsDomain]: reportsDomainColumns,
            [logs]: logsColumns,
            [deals]: dealsColumns,
            [feedFilters]: gridColumns[feedFilters],
            [usersProfile]: gridColumns[usersProfile]
        };
    }
);

export const selectGridColumnByViewType = createSelector(
    selectColumns,
    selectGridSourceParams,
    (
        columnsMap: Record<
            | campaigns
            | groups
            | banners
            | retargetings
            | phrases
            | logs
            | deals
            | reportsClient
            | reportsDomain,
            Array<GridColumnDecl<GridRow>>
        >,
        sourceParams: GridSourceParams
    ) => {
        const { view } = sourceParams;
        return columnsMap[view];
    }
);

export const selectGridGroupedColumns = createSelector(
    selectGridColumnByViewType,
    (state: State) => state.grid.columnsSettings,
    selectGridSourceParams,
    (state: State) => state.goalsData.goals,
    (state: State) => state.grid.clientInfo,
    (state: State) => state.commonData.features.isGoalsStatEnabled,
    (state: State) => state.grid.goalsSettings,
    (
        columns: Array<GridColumnDecl<Campaign | Group | Banner | Phrase>>,
        columnsSettings,
        sourceParams,
        goals,
        clientInfo,
        isGoalsStatEnabled,
        goalsSettings
    ) => {
        if (sourceParams === undefined) {
            throw new Error('не определены sourceParams');
        }

        const { workCurrency } = clientInfo;
        const goalIds = getActiveItemsIds(goalsSettings) || [];
        const { view } = sourceParams;

        const currentColumnSettings = columnsSettings[view];
        const { columnOrder } = currentColumnSettings;

        const sortMapGroupedColumn: Record<
            string,
            Array<GridGroupedColumn<Campaign | Group | Banner | Phrase>>
        > = {};

        const groupedColumnsGoals = getGroupedColumnsGoals(
            goalIds,
            workCurrency,
            columnsSettings,
            sourceParams,
            goals
        );

        const goalsColumnViewInfo =
            columnsSettings[view].columnViewInfo[GroupedColumnKey.GOALS];
        if (
            groupedColumnsGoals.length === 0 ||
            goalsColumnViewInfo === undefined ||
            goalsColumnViewInfo.hidden ||
            !isGoalsStatEnabled
        ) {
            return [];
        }

        columns.forEach(column => {
            sortMapGroupedColumn[column.key] = [
                {
                    title: '',
                    key: column.key,
                    columns: [column],
                    fixed: column.fixed
                }
            ];
        });

        sortMapGroupedColumn[GroupedColumnKey.GOALS] = groupedColumnsGoals;

        return columnOrder.reduce(
            (
                acc: Array<
                    GridGroupedColumn<Campaign | Group | Banner | Phrase>
                >,
                key
            ) => {
                const listGroupedColumn = sortMapGroupedColumn[key];

                if (
                    listGroupedColumn &&
                    !currentColumnSettings.columnViewInfo[key].hidden
                ) {
                    acc = acc.concat(listGroupedColumn);
                }

                return acc;
            },
            []
        );
    }
);

export const selectGridData = createSelector(
    selectGridColumnByViewType,
    selectGridGroupedColumns,
    (columns, groupedColumns) => {
        return {
            columns: columns.filter(
                (column: GridColumnDecl<GridRow>) => !column.hidden
            ),
            groupedColumns
        };
    }
);

export const selectIsFilterChangedFlag = createSelector(
    (state: State, query: Query) => query,
    selectGridSourceParams,
    (state: State) => state.grid.gridData.totalStats,
    (
        oldParams: Query,
        newParams: GridSourceParams,
        totalStats?: GridTotalStatsInfo
    ): boolean => {
        const { view } = newParams;

        let isFilterParamsChanged = get(oldParams, 'view') !== view;

        if (!isFilterParamsChanged) {
            const newFilter = newParams.filter;

            if (oldParams !== undefined) {
                const oldFilter = oldParams.filter;

                if (oldFilter && newFilter) {
                    isFilterParamsChanged =
                        newFilter.length !== oldFilter.length;
                } else if (
                    oldFilter === undefined &&
                    newFilter === undefined &&
                    totalStats === undefined
                ) {
                    isFilterParamsChanged = true;
                } else if (
                    (oldFilter === undefined && newFilter) ||
                    (oldFilter && newFilter === undefined)
                ) {
                    isFilterParamsChanged = true;
                }
            }
        }

        return isFilterParamsChanged;
    }
);

export const selectPreselectedCampaign = createSelector(
    (state: State) => state.router.route,
    (state: State) => state.grid.selectionInfo.campaigns,
    (state: State) => state.grid.selectionInfo.groups,
    selectGridSourceParamsView,
    (state: State) => state.grid.gridData,
    (state: State, items: SelectorItems[]) => items,
    (
        route: string,
        campaignsSelection: string[],
        groupsSelection: string[],
        gridView: GridViewType | undefined,
        gridData: GridData,
        items: SelectorItems[]
    ): SelectorItems | undefined => {
        if (!route.startsWith(RoutesPath.shortGrid)) {
            return undefined;
        }

        if (campaignsSelection.length === 1) {
            const [campaignId] = campaignsSelection;
            return items.find(el => el.id === campaignId);
        }

        if (gridView === campaigns && gridData.data.length === 1) {
            const [{ id: campaignId }] = gridData.data;
            return items.find(el => el.id === campaignId);
        }

        if (gridView === groups) {
            let groupId: string | undefined;

            if (groupsSelection.length === 1) {
                [groupId] = groupsSelection;
            }

            if (gridData.data.length === 1) {
                groupId = gridData.data[0].id;
            }

            if (!groupId) {
                return undefined;
            }

            const group = gridData.data.find(el => el && el.id === groupId) as
                | GroupBaseInfo
                | undefined;

            if (!group) {
                return undefined;
            }

            return items.find(el => el.id === group.campaign.id);
        }

        return undefined;
    }
);

export const selectPreselectedGroup = createSelector(
    (state: State) => state.router.route,
    (state: State) => state.grid.selectionInfo.groups,
    selectGridSourceParamsView,
    (state: State) => state.grid.gridData,
    (state: State, items: SelectorItems[]) => items,
    (
        route: string,
        groupsSelection: string[],
        gridView: GridViewType | undefined,
        gridData: GridData,
        items: SelectorItems[]
    ): SelectorItems | undefined => {
        if (!route.startsWith(RoutesPath.shortGrid)) {
            return undefined;
        }

        if (groupsSelection.length === 1) {
            const [groupId] = groupsSelection;
            return items.find(el => el.id === groupId);
        }

        if (gridView === groups && gridData.data.length === 1) {
            const [{ id: campaignId }] = gridData.data;
            return items.find(el => el.id === campaignId);
        }

        return undefined;
    }
);

export const selectGridColumnsForColumnsSettings = createSelector(
    (state: State) => state.commonData.constants,
    (state: State) => state.commonData.client.ClientID,
    (state: State) => state.commonData.wallet,
    (state: State) => state.commonData.tld,
    (state: State) => state.commonData.operator,
    (state: State) => state.grid.clientInfo,
    (state: State) => state.grid.columnsSettings,
    selectGridSourceParams,
    (state: State) => state.grid.isCompactViewEnabled,
    (state: State) => state.commonData.features,
    (state: State) => state.grid.campaignsCalcTypeFilter,
    (state: State) => state.columnsSettingsEditor.goalsSettings,
    (
        constants: ConstData | undefined,
        clientId: string | undefined,
        wallet: WalletData,
        tld: Tld,
        operator: OperatorData,
        clientInfo: ClientInfo,
        columnsSettings: ColumnsSettingsViewDict,
        sourceParams: GridSourceParams | undefined,
        isCompactViewEnabled: boolean,
        clientFeatures: ClientFeatures,
        campaignsCalcTypeFilter?: CampaignsCalcTypeFilter,
        goalsSettings?: GoalDecl[]
    ) => {
        return getGridColumns(
            constants,
            clientId,
            wallet,
            tld,
            operator,
            clientInfo,
            columnsSettings,
            sourceParams,
            isCompactViewEnabled,
            clientFeatures,
            campaignsCalcTypeFilter,
            goalsSettings,
            true
        );
    }
);

export const selectColumnsForColumnsSettingsEditor = createSelector(
    selectGridColumnsForColumnsSettings,
    selectLogsColumns,
    selectReportsClientColumns,
    selectReportsDomainColumns,
    selectDealsColumns,
    (
        gridColumns,
        logsColumns,
        reportsClientColumns,
        reportsDomainColumns,
        dealsColumns
    ): Record<
        | campaigns
        | groups
        | banners
        | phrases
        | retargetings
        | logs
        | deals
        | reportsClient
        | reportsDomain
        | feedFilters
        | usersProfile,
        Array<GridColumnDecl<GridRow>>
    > => {
        return {
            [campaigns]: gridColumns[campaigns],
            [groups]: gridColumns[groups],
            [phrases]: gridColumns[phrases],
            [banners]: gridColumns[banners],
            [retargetings]: gridColumns[retargetings],
            [reportsClient]: reportsClientColumns,
            [reportsDomain]: reportsDomainColumns,
            [logs]: logsColumns,
            [deals]: dealsColumns,
            [feedFilters]: gridColumns[feedFilters],
            [usersProfile]: gridColumns[usersProfile]
        };
    }
);

export const selectColumnsSettings = createSelector(
    (state: State) => state.commonData.features.isGoalsStatEnabled,
    (state: State) => state.columnsSettingsEditor.columnsSettings,
    selectColumnsForColumnsSettingsEditor,
    (
        isGoalsStatEnabled: boolean,
        columnsSettings: ColumnsSettingsViewDict,
        gridColumns: Record<
            | campaigns
            | groups
            | banners
            | phrases
            | logs
            | deals
            | reportsClient
            | reportsDomain
            | retargetings
            | feedFilters
            | usersProfile,
            Array<GridColumnDecl<GridRow>>
        >
    ) => {
        const columnsMap: Record<
            | campaigns
            | groups
            | banners
            | phrases
            | logs
            | deals
            | reportsClient
            | reportsDomain
            | retargetings
            | feedFilters
            | usersProfile,
            Array<GridColumnDecl<GridRow>>
        > = {
            [campaigns]: [],
            [groups]: [],
            [banners]: [],
            [phrases]: [],
            [logs]: [],
            [deals]: [],
            [reportsClient]: [],
            [reportsDomain]: [],
            [retargetings]: [],
            [feedFilters]: [],
            [usersProfile]: []
        };

        Object.keys(columnsSettings).forEach(keyGridType => {
            const listGridColumns: Array<GridColumnDecl<GridRow>> =
                gridColumns[keyGridType];
            const preparedListGridColumns = listGridColumns.reduce(
                (acc: Array<GridColumnDecl<GridRow>>, gridColumn) => {
                    const keyColumn = gridColumn.key;
                    const viewInfoCurrentColumn =
                        columnsSettings[keyGridType].columnViewInfo[keyColumn];

                    if (
                        keyColumn !== GroupedColumnKey.GOALS ||
                        (keyColumn === GroupedColumnKey.GOALS &&
                            isGoalsStatEnabled)
                    ) {
                        acc.push({
                            ...gridColumn,
                            hidden: Boolean(
                                viewInfoCurrentColumn &&
                                    viewInfoCurrentColumn.hidden
                            ),
                            title: gridColumn.titleSettings || gridColumn.title
                        });
                    }

                    return acc;
                },
                []
            );

            columnsMap[keyGridType] = arrangeInOrder(
                preparedListGridColumns,
                columnsSettings[keyGridType].columnOrder,
                true
            );
        });

        return columnsMap;
    }
);

export const selectGoalsSettingsForColumnsSettingEditor = createSelector(
    (state: State) => state.goalsData.goals,
    (state: State) => state.grid.goalsSettings,
    getGoalsSettingsForColumnsSettingsEditor
);
